// Auth client — wraps fetch para anexar Bearer + Argon2id browser-side.
//
// Senha do membro vai client-side via Argon2id (D-98 — paridade com intake).
// Salt = sha256(email_normalizado). Servidor recebe authHash[0..32] em base64;
// kekRaw[32..64] permanece no browser (NÃO é usado em login mas pode ser
// reutilizado para unwrap de DEK de caso atribuído quando KEK_membro entrar
// em Phase 6 — para Phase 2-MVP usamos KEK_comite global no servidor).

import { deriveKeyMaterial, splitKeyMaterial, rewrapMemberPrivateKey } from '@aions/crypto';
import { apiUrl } from './api-base';
// Contrato de wire do liveness — cópia única, compartilhada com o web-admin (24/08).
import {
  parseChallengeResponse,
  parseVerifyResponse,
  erroDeTransporte,
  type ChallengeResult,
  type LivenessOutcome,
} from '@aions/ui-primitives';
import { bytesToBase64 } from './bytes';
import { clearKekMembro, deriveKekMembro, storeKekMembro } from './kek-member-session';

const STORAGE_KEY = 'aions:comite:session:v1';

export interface SessionState {
  token: string;
  user: {
    id: string;
    email: string;
    roles: string[];
    /** Phase 2.1 D-190 — base64 de 32 bytes; salt para Argon2id KEK_membro. */
    kekSalt?: string;
    /** ADR-0010 Fatia 1 — true se o membro já tem keypair enrolado. O LoginPage
     *  só dispara o backfill quando false/undefined (idempotência client-side;
     *  o servidor first-writer-wins é o guard real). NUNCA o material de chave. */
    comiteKeypairEnrolled?: boolean;
    /** Sprint 3.1 — true se o user já aceitou a versão atual de LGP-02. False
     *  abre PolicyAcceptanceModal pré-emptivamente (bloqueia o dashboard). */
    policyAccepted?: boolean;
    /** Sprint 3.1 — versão canônica atual (echoed pelo /login para evitar drift
     *  entre cliente buildado e server). */
    policyVersion?: string;
  };
  expInactivity?: number;
}

let memSession: SessionState | null = null;

export function getStoredSession(): SessionState | null {
  if (memSession) return memSession;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionState;
    memSession = parsed;
    return parsed;
  } catch {
    return null;
  }
}

export function setSession(s: SessionState | null): void {
  memSession = s;
  if (s) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch {
      // sessionStorage indisponível — degradar para memória apenas.
    }
  } else {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

/** Hashes the password client-side using the same Argon2id pipeline as intake. */
async function deriveAuthHash(email: string, password: string): Promise<string> {
  const material = await deriveKeyMaterial(password, email.trim().toLowerCase());
  const { authHash } = splitKeyMaterial(material);
  return bytesToBase64(authHash);
}

export interface LoginResult {
  ok: boolean;
  error?: string;
  session?: SessionState;
}

export async function loginWithPassword(
  email: string,
  password: string,
  totpCode: string,
): Promise<LoginResult> {
  let authHashB64: string;
  try {
    authHashB64 = await deriveAuthHash(email, password);
  } catch (err) {
    void err;
    return { ok: false, error: 'network' };
  }

  let resp: Response;
  try {
    resp = await fetch(apiUrl('/api/comite/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase(), authHash: authHashB64, totpCode }),
    });
  } catch {
    return { ok: false, error: 'network' };
  }

  if (resp.status === 423) return { ok: false, error: 'account_locked' };
  if (!resp.ok) return { ok: false, error: 'invalid_credentials' };

  const data = (await resp.json()) as {
    token: string;
    user: {
      id: string;
      email: string;
      roles: string[];
      kekSalt?: string;
      comiteKeypairEnrolled?: boolean;
      policyAccepted?: boolean;
      policyVersion?: string;
    };
  };
  const session: SessionState = { token: data.token, user: data.user };
  setSession(session);
  return { ok: true, session };
}

/** Sprint 3.1 — POST /policy-accept; em sucesso, atualiza policyAccepted=true na session. */
export async function acceptPolicy(): Promise<{ ok: boolean; error?: string }> {
  const s = getStoredSession();
  if (!s) return { ok: false, error: 'not_authenticated' };
  try {
    const resp = await fetch(apiUrl('/api/comite/policy-accept'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${s.token}` },
    });
    if (!resp.ok) {
      return { ok: false, error: `http_${String(resp.status)}` };
    }
    const data = (await resp.json()) as { policyVersion: string };
    setSession({
      ...s,
      user: { ...s.user, policyAccepted: true, policyVersion: data.policyVersion },
    });
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  }
}

export async function logout(): Promise<void> {
  const s = getStoredSession();
  if (s) {
    try {
      await fetch(apiUrl('/api/comite/login/logout'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${s.token}` },
      });
    } catch {
      // silently ignore — local-side cleanup ainda acontece
    }
  }
  setSession(null);
  // Phase 2.1 D-191 — logout zera KEK_membro do sessionStorage + memória.
  clearKekMembro();
}

export interface ChangePasswordResult {
  ok: boolean;
  error?: string;
}

/**
 * F2 Parte A — troca de senha autenticada. Deriva o authHash ATUAL e o NOVO
 * (Argon2id client-side, mesmo pipeline do login) e POSTa para
 * /api/comite/change-password. O servidor exige os 2 fatores atuais (senha +
 * TOTP). Senha NUNCA trafega em claro.
 */
export async function changePassword(
  currentPassword: string,
  newPassword: string,
  totpCode: string,
): Promise<ChangePasswordResult> {
  const s = getStoredSession();
  if (!s) return { ok: false, error: 'not_authenticated' };
  let currentAuthHash: string;
  let newAuthHash: string;
  try {
    currentAuthHash = await deriveAuthHash(s.user.email, currentPassword);
    newAuthHash = await deriveAuthHash(s.user.email, newPassword);
  } catch {
    return { ok: false, error: 'network' };
  }
  // ── Caminho (a), 28/07 — RE-EMBRULHO DA PRIVADA ─────────────────────────────
  // A KEK_membro deriva da SENHA. Sem re-embrulhar, trocar a senha torna a privada
  // inabrível e o membro perde a leitura de TODAS as denúncias, para sempre
  // (provado em lib/troca-senha-destroi-acesso.test.ts). Aqui é o único lugar do
  // sistema onde as duas senhas coexistem, então é o único lugar onde isso tem cura
  // sem re-wrap por caso: a PÚBLICA não muda, os wraps dos casos seguem válidos.
  //
  // ORDEM É PARTE DA CORREÇÃO: buscar → derivar as duas KEKs → re-embrulhar →
  // SÓ ENTÃO um POST único. Falhou o desembrulho? ABORTA sem enviar nada — mandar a
  // senha nova com o ciphertext velho seria o bug original com um passo a mais.
  const salt = s.user.kekSalt;
  let kekNova: Uint8Array | null = null;
  let reembrulho: { privateKeyCiphertext: string; nonce: string } | null = null;
  try {
    const respKp = await apiFetch('/api/comite/my-keypair');
    if (respKp.status === 404) {
      // Flag de leitura OFF (decisão (ii), 28/07): FALHA ALTO em vez de gravar a
      // senha e matar a keypair em silêncio. O membro vê mensagem, não código.
      return { ok: false, error: 'leitura_desabilitada' };
    }
    if (respKp.ok) {
      if (!salt) return { ok: false, error: 'missing_kek_salt' };
      const kp = (await respKp.json()) as { ciphertext?: string; nonce?: string };
      if (typeof kp.ciphertext !== 'string' || typeof kp.nonce !== 'string') {
        return { ok: false, error: 'keypair_indisponivel' };
      }
      const kekAntiga = await deriveKekMembro(currentPassword, salt);
      kekNova = await deriveKekMembro(newPassword, salt);
      try {
        reembrulho = await rewrapMemberPrivateKey(kp.ciphertext, kp.nonce, kekAntiga, kekNova);
      } finally {
        kekAntiga.fill(0);
      }
    }
    // respKp 4xx/5xx que não seja 404: membro sem keypair enrolada → segue sem
    // re-embrulho (o servidor é a autoridade e exige AUSÊNCIA dos campos nesse caso).
  } catch {
    kekNova?.fill(0);
    return { ok: false, error: 'reembrulho_falhou' };
  }

  try {
    const resp = await apiFetch('/api/comite/change-password', {
      method: 'POST',
      body: JSON.stringify({
        currentAuthHash,
        newAuthHash,
        totpCode,
        ...(reembrulho
          ? {
              privateKeyCiphertext: reembrulho.privateKeyCiphertext,
              privateKeyNonce: reembrulho.nonce,
            }
          : {}),
      }),
    });
    if (!resp.ok) {
      const data = (await resp.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? 'submit_failed' };
    }
    // A KEK da sessão foi derivada da senha VELHA. Sem trocá-la aqui, o membro
    // troca a senha e perde a leitura ATÉ RELOGAR — o mesmo sintoma que esta
    // correção existe para eliminar, causado pela própria correção.
    if (kekNova) await storeKekMembro(kekNova);
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  } finally {
    kekNova?.fill(0);
  }
}

/** fetch wrapper that injects Bearer + auto-refreshes session on X-Session-Refresh header. */
export async function apiFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const s = getStoredSession();
  const headers = new Headers(init.headers);
  if (s) headers.set('Authorization', `Bearer ${s.token}`);
  if (!headers.has('Content-Type') && init.body && typeof init.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  const resp = await fetch(apiUrl(path), { ...init, headers });
  // Sliding refresh — server emits header on every authenticated request
  const refreshed = resp.headers.get('X-Session-Refresh');
  if (refreshed && s) {
    setSession({ ...s, token: refreshed });
  }
  if (resp.status === 401) {
    setSession(null);
    // BUG 3 — 401 em ação autenticada = sessão expirada. O `storage` event não
    // dispara na mesma aba, então sinalizamos o SessionContext explicitamente
    // para ele re-ler getStoredSession() (→ null) e o App ir pro login —
    // em vez de o componente traduzir o 401 como "Falha ao [ação]".
    window.dispatchEvent(new Event('comite:session-expired'));
  }
  return resp;
}

// =====================================================================
// ADR-0009 Peça 6 — liveness no login (challenge → vídeo → verify).
// =====================================================================

/** POST /api/comite/liveness/challenge — sorteia direção + token single-use. */
export async function drawLivenessChallenge(): Promise<ChallengeResult> {
  try {
    return await parseChallengeResponse(
      await apiFetch('/api/comite/liveness/challenge', { method: 'POST' }),
    );
  } catch {
    return { ok: false, error: 'network' };
  }
}

/**
 * POST /api/comite/liveness/verify (multipart vídeo + challengeToken).
 *
 * 🔴 A TRADUÇÃO da resposta mudou de casa em 24/08: mora em `@aions/ui-primitives`
 * (`liveness-contract`), compartilhada com o web-admin. Aqui fica só o TRANSPORTE — qual
 * endpoint, qual `apiFetch`, qual storage de sessão. Um status novo no servidor passa a ser
 * corrigido em UM lugar, e não em dois que divergem em silêncio.
 */
export async function verifyLiveness(
  challengeToken: string,
  video: Blob,
  signal?: AbortSignal,
): Promise<LivenessOutcome> {
  const fd = new FormData();
  fd.append('video', video, 'verificacao.webm');
  fd.append('challengeToken', challengeToken);

  let resp: Response;
  try {
    resp = await apiFetch('/api/comite/liveness/verify', {
      method: 'POST',
      body: fd,
      ...(signal ? { signal } : {}),
    });
  } catch (e) {
    return erroDeTransporte(e);
  }

  return parseVerifyResponse(resp, (token) => {
    const atual = getStoredSession();
    if (atual) setSession({ ...atual, token });
  });
}

export interface FirstAccessResult {
  ok: boolean;
  error?: string;
}

/**
 * F3-C — ativação de primeiro acesso (antes inline na FirstAccessPage). Deriva o
 * authHash (Argon2id client-side) e POSTa /api/comite/first-access com
 * Bearer=firstAccessToken (JWT one-shot, role-agnóstico). PRÉ-AUTH: fetch cru.
 * Senha NUNCA trafega em claro.
 */
export async function activateFirstAccess(
  token: string,
  email: string,
  password: string,
  totpCode: string,
  foto?: Blob,
): Promise<FirstAccessResult> {
  try {
    const authHash = await deriveAuthHash(email, password);
    // CERIMÔNIA ATÔMICA (ADR-0022, 24/08) — com foto, tudo vai num multipart só, e o
    // servidor grava senha e template no MESMO save. Sem foto, o corpo segue JSON: é o
    // caminho de quem não é `comite_membro` (admin_ti) ou de ambiente com a biometria
    // desligada. Não são duas portas — é o mesmo endpoint, e é ele que gasta o link.
    //
    // ⚠️ NÃO mande `Content-Type` no multipart: o browser precisa gerar o boundary. Fixar
    // 'multipart/form-data' à mão produz um corpo que o servidor não consegue parsear, e
    // o sintoma é um 400 `invalid_multipart` que parece erro de foto.
    let resp: Response;
    if (foto !== undefined) {
      const form = new FormData();
      form.append('authHash', authHash);
      form.append('totpCode', totpCode);
      form.append('foto', foto, 'cadastro.jpg');
      resp = await fetch(apiUrl('/api/comite/first-access'), {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
    } else {
      resp = await fetch(apiUrl('/api/comite/first-access'), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ authHash, totpCode }),
      });
    }
    if (!resp.ok) {
      const data = (await resp.json().catch(() => ({}))) as { error?: string };
      return { ok: false, error: data.error ?? 'generic' };
    }
    return { ok: true };
  } catch {
    return { ok: false, error: 'network' };
  }
}

// Re-export para os consumidores que já importavam estes tipos deste módulo.
export type { ChallengeResult, LivenessOutcome };
